package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.core.SortableTable
import com.sddamico.swigmore.ds.core.TableColumn
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.forms.SelectField
import com.sddamico.swigmore.ds.poll.MatchupCard
import com.sddamico.swigmore.ds.poll.MatchupSide
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeTeam
import com.sddamico.swigmore.models.GameOutcome
import com.sddamico.swigmore.models.GameResultsResponse
import com.sddamico.swigmore.models.HeadToHeadDto
import com.sddamico.swigmore.models.PollRules
import com.sddamico.swigmore.models.ResultTeam
import com.sddamico.swigmore.models.TeamRecord
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.CachedResource
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.NOT_FINAL
import com.sddamico.swigmore.util.fixed
import com.sddamico.swigmore.util.minuteClock
import com.sddamico.swigmore.util.rememberCachedResource
import com.sddamico.swigmore.util.word
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** Every scored game: one week at a time, one team's season, or who each team beat and lost to. */
@Page
@Composable
fun ResultsPage() {
  SignedInPage("Results", activeTab = AppTab.Results) {
    ContentColumn {
      when (val results = rememberCachedResource(CachedResource.Results).state) {
        LoadState.Loading -> ResultsSkeleton()
        is LoadState.Failed -> Reveal { PageTitle("Results", "Game Results", lede = results.message) }
        is LoadState.Ready -> Reveal { Results(results.value) }
      }
    }
  }
}

/** What the page lists: one week's games, one team's season, or every team's head-to-head. */
private sealed interface ResultsView {
  data object AllTeams : ResultsView
  data class Team(val id: String) : ResultsView
  data object HeadToHead : ResultsView
}

// The select holds strings; Fantrax team ids never contain a space, so this can't collide with one.
private const val HEAD_TO_HEAD_OPTION = "head to head"

@Composable
private fun Results(results: GameResultsResponse) {
  val games = results.games
  val weeks = games.map { it.week }.distinct()
  var week by remember { mutableStateOf(weeks.lastOrNull()) }
  var view by remember { mutableStateOf<ResultsView>(ResultsView.AllTeams) }

  PageTitle(
    eyebrow = weeks.lastOrNull()?.let { "Through week $it · via Fantrax" } ?: "Results",
    title = "Game Results",
    lede = when {
      games.isEmpty() -> "Scores land here at ${minuteClock(PollRules.SCORES_RELEASE_HOUR * 60)} ET the Sunday after the first week's games."
      games.any { !it.final } -> "Scores marked not final can still change until Fantrax closes the week."
      else -> null
    },
  )
  if (games.isEmpty()) return

  val teams = results.headToHead.map { it.team.id to it.team.name }
  // Brian Underwood's head-to-head sheet, for checking tiebreaks between teams.
  val options = teams + (HEAD_TO_HEAD_OPTION to "Brian's view")
  val selected = when (val v = view) {
    ResultsView.AllTeams -> null
    is ResultsView.Team -> v.id
    ResultsView.HeadToHead -> HEAD_TO_HEAD_OPTION
  }
  Div(attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "align-items" to "center", "gap" to "12px", "margin-top" to "20px").toAttrs()) {
    Div(attrs = Modifier.css("flex" to "0 1 320px", "min-width" to "0").toAttrs()) {
      SelectField(selected, { option ->
        view = when (option) {
          null -> ResultsView.AllTeams
          HEAD_TO_HEAD_OPTION -> ResultsView.HeadToHead
          else -> ResultsView.Team(option)
        }
      }, options, placeholder = "All teams", label = "Team")
    }
    if (view == ResultsView.AllTeams) {
      Div(attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "gap" to "8px").toAttrs()) {
        weeks.forEach { w ->
          Button("Wk $w", variant = if (w == week) ButtonVariant.Cream else ButtonVariant.Outline,
            style = ButtonStyle.CapsSm, onDark = true, onClick = { week = w })
        }
      }
    }
  }

  when (val v = view) {
    ResultsView.AllTeams -> GameGrid {
      games.filter { it.week == week }.forEach { game ->
        MatchupCard(tag = "", note = if (game.final) "" else NOT_FINAL, home = side(game.home), away = side(game.away))
      }
    }
    is ResultsView.Team -> GameGrid {
      games.filter { it.home.teamId == v.id || it.away.teamId == v.id }.forEach { game ->
        val (team, opponent) = if (game.home.teamId == v.id) game.home to game.away else game.away to game.home
        val outcome = team.outcome.word
        // The card lists away first, so the picked team goes there to sit on top of every card.
        MatchupCard(tag = "Week ${game.week}", note = if (game.final) outcome else "$outcome · $NOT_FINAL", home = side(opponent), away = side(team))
      }
    }
    ResultsView.HeadToHead -> SortableTable(headToHeadColumns, results.headToHead, Modifier.css("margin" to "20px 0 28px"),
      initialSort = headToHeadColumns.indexOf(beatColumn))
  }
}

@Composable
private fun GameGrid(content: @Composable () -> Unit) {
  Div(
    attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fill,minmax(min(100%,300px),1fr))",
      "gap" to "10px", "margin" to "20px 0 28px").toAttrs()
  ) { content() }
}

private fun side(team: ResultTeam) =
  MatchupSide(team.name, team.record.toString(), team.standing, fixed(team.score, 1), winner = team.outcome == GameOutcome.WIN)

private val HeadToHeadDto.record get() =
  TeamRecord(opponents.sumOf { it.wins }, opponents.sumOf { it.losses }, opponents.sumOf { it.ties })

// Tied opponents are listed under both Beat and Lost to, so each sort breaks equal counts by ties.
private val beatColumn = TableColumn("Beat", compareByDescending<HeadToHeadDto> { it.record.wins }.thenByDescending { it.record.ties }, alignEnd = false) { row ->
  OpponentList(row.opponents.filter { it.wins > 0 || it.ties > 0 }.map { meetingLabel(it.opponent.name, it.wins, it.ties) })
}

private val headToHeadColumns = listOf(
  TableColumn<HeadToHeadDto>("Team", compareBy { it.team.name.lowercase() }, widthPx = 110, alignEnd = false, sticky = true) {
    Div(attrs = Modifier.typeTeam("13px").css("white-space" to "normal", "width" to "clamp(110px, 14vw, 180px)", "overflow-wrap" to "break-word").toAttrs()) {
      Text(it.team.name)
    }
    Div(attrs = Modifier.typeBody("12px", 500, "normal").css("color" to SwColor.textMuted, "margin-top" to "2px").toAttrs()) {
      Text(it.record.toString())
    }
  },
  beatColumn,
  TableColumn("Lost to", compareBy<HeadToHeadDto> { it.record.losses }.thenBy { it.record.ties }, alignEnd = false) { row ->
    OpponentList(row.opponents.filter { it.losses > 0 || it.ties > 0 }.map { meetingLabel(it.opponent.name, it.losses, it.ties) })
  },
)

private fun meetingLabel(opponent: String, times: Int, ties: Int): String {
  val count = if (times > 1) " ×$times" else ""
  val tied = when {
    ties == 0 -> ""
    times == 0 -> if (ties == 1) " (tie)" else " ($ties ties)"
    else -> if (ties == 1) " and a tie" else " and $ties ties"
  }
  return opponent + count + tied
}

@Composable
private fun OpponentList(labels: List<String>) {
  Div(
    attrs = Modifier.typeBody("13px", lineHeight = "1.5")
      .css("white-space" to "normal", "min-width" to "190px", "color" to if (labels.isEmpty()) SwColor.textMuted else "inherit")
      .toAttrs()
  ) { Text(if (labels.isEmpty()) "—" else labels.joinToString(", ")) }
}

@Composable
private fun ResultsSkeleton() {
  Skeleton {
    PageTitleSkeleton(lede = false)
    SkeletonBlock("44px", Modifier.css("margin-top" to "20px"), width = "320px")
    Div(
      attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fill,minmax(min(100%,300px),1fr))",
        "gap" to "10px", "margin" to "20px 0 28px").toAttrs()
    ) {
      repeat(6) { SkeletonBlock("112px") }
    }
  }
}
